import React, { useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { AddCircleOutline, Cancel, Close, VerifiedUserOutlined } from "@mui/icons-material";
import CustomRadioButton from "../../common/CustomRadioButton";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import {
  useCodetablesSlice,
  CodeTable,
  getCodeTableValuesByTableName,
} from "../../../redux/slice/codetableSlice";
import { useTheme } from "@mui/material/styles";
import { useModalStyles } from "../../common/useModalStyles";

export type AuthorizedSignerInfo = {
  index?: number;
  title: string;
  fullName: string;
  email: string;
  isOwner: string;
  position?: string;
  inactiveDate?: Date;
  otherTitle?: string;
};

interface AuthorizedSignerProps {
  open: boolean;
  onClose: () => void;
  addAuthorizedSigner: (authorizedSigner: AuthorizedSignerInfo) => void;
  onChange?: (name: string, value: string) => void;
  authorizedSignerData?: AuthorizedSignerInfo;
}

export const authorizedSignerInitialState: any = {
  title: "",
  fullName: "",
  email: "",
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const AuthorizedSignerPopup: React.FC<AuthorizedSignerProps> = ({
  open,
  onClose,
  addAuthorizedSigner,
  onChange,
  authorizedSignerData,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { tokens, paperSx, closeIconSx , cancelBtnSx, primaryBtnSx, fieldWrapSx} = useModalStyles();

  const { control, handleSubmit, reset, watch, formState: { errors } } =
    useForm<AuthorizedSignerInfo>();

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [personTitleList, setPersonTitleList] = React.useState<CodeTable[]>([]);
  const [positionList, setPositionList] = React.useState<CodeTable[]>([]);

  const personTitleListOptions = useMemo(() => {
    return personTitleList
      .map((item) => ({ label: item.displayValue, value: item.keyName }))
      .sort((a, b) =>
        a.value === "OTHER" ? 1 : b.value === "OTHER" ? -1 : 0
      );
  }, [personTitleList]);

  const positionListOptions = useMemo(() => {
    return positionList.map((item) => ({
      label: item.displayValue,
      value: item.keyName,
    }));
  }, [positionList]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      setPersonTitleList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PersonTitle")
      );
      const sorted = [
        ...getCodeTableValuesByTableName(
          allCodeTablesValuesMap,
          "SignerPosition"
        ),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setPositionList(sorted);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (authorizedSignerData) {
      if (
        authorizedSignerData.otherTitle !== null &&
        authorizedSignerData.otherTitle !== undefined &&
        authorizedSignerData.otherTitle.length > 0
      ) {
        authorizedSignerData.otherTitle = authorizedSignerData.title;
        authorizedSignerData.title = "OTHER";
      }
      reset(authorizedSignerData);
    }
    return () => {
      reset(authorizedSignerInitialState);
    };
  }, []);

  const onSubmit = (data: AuthorizedSignerInfo) => {
    const dataToSubmit = {
      ...data,
      title:
        data.title === "OTHER" ? data.otherTitle || "" : data.title || "",
      otherTitle:
        data.title === "OTHER" ? data.otherTitle || "" : undefined,
    };
    addAuthorizedSigner(dataToSubmit);
    reset(authorizedSignerInitialState);
    onClose();
  };

  const isEditing = !!authorizedSignerData;

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.88rem",
        fontWeight: 600,
        color: tokens.textPrimary,
        mb: 0.8,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const radioSx = {
    color: tokens.textSecondary,
    "&.Mui-checked": { color: tokens.accent },
    padding: "4px 8px",
  };

  const radioLabelSx = {
    fontSize: "0.85rem",
    color: tokens.textPrimary,
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.2,
            borderBottom: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary }}
          >
            {isEditing ? "Edit Authorized Signer" : "Add Authorized Signer"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={closeIconSx}>
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <DialogContent
          sx={{ px: 3, py: 3, overflowY: "auto", flex: 1, minHeight: 0 }}
        >
          <Grid container spacing={2}>

            {/* Row 1 — Full Name */}
            <Grid item xs={12}>
              <FieldLabel label="Full Name" required />
              <Box sx={fieldWrapSx(!!errors?.fullName)}>
              <CustomTextField
                control={control}
                controllerName="fullName"
                label=""
                size="small"
                placeholder="Full name"
                rules={{ required: "Full Name is required." }}
              />
              </Box>
            </Grid>

            {/* Row 2 — Email */}
            <Grid item xs={12}>
              <FieldLabel label="Email" required />
              <Box sx={fieldWrapSx(!!errors?.email)}>
              <CustomTextField
                control={control}
                controllerName="email"
                label=""
                size="small"
                placeholder="email@example.com"
                rules={{
                  required: "Email is required.",
                  pattern: {
                    value:
                      /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                    message: "Invalid email.",
                  },
                }}
              />
              </Box>
            </Grid>

            {/* Row 3 — Title (narrow) + Specified Title (if OTHER) */}
            <Grid item xs={12} sm={4}>
              <FieldLabel label="Title" required />
              <Box sx={fieldWrapSx(!!errors?.title)}>
              <CustomDropdownField
                name="title"
                control={control}
                options={personTitleListOptions}
                label=""
                size="small"
                placeholderText="Title"
                rules={{ required: "Title is required." }}
                sortOptions={false}
              />
              </Box>
            </Grid>

            {watch("title") === "OTHER" && (
              <Grid item xs={12} sm={4}>
                <FieldLabel label="Specified Title" required />
                <Box sx={fieldWrapSx(!!errors?.otherTitle)}>
                <CustomTextField
                  control={control}
                  controllerName="otherTitle"
                  label=""
                  size="small"
                  placeholder="Specified title"
                  maxLength={10}
                  rules={{ required: "Specified title is required." }}
                />
                </Box>
              </Grid>
            )}

            {/* Row 4 — Position */}
            <Grid item xs={12}>
              <FieldLabel label="Position" required />
              <Box sx={fieldWrapSx(!!errors?.position)}>
              <CustomDropdownField
                name="position"
                control={control}
                options={positionListOptions}
                label=""
                size="small"
                placeholderText="Select position"
                rules={{ required: "Position is required." }}
              />
              </Box>
            </Grid>

            {/* Row 5 — Ownership radio */}
            <Grid item xs={12}>
              <FieldLabel label="Ownership" />
              <CustomRadioButton
                control={control}
                controllerName="isOwner"
                radioSx={radioSx}
                radioLabelSx={radioLabelSx}
                options={[
                  { value: "YES", label: "Business Owner" },
                  { value: "NO", label: "Non-Business Owner" },
                ]}
                defaultValue="YES"
              />
            </Grid>

          </Grid>
        </DialogContent>

        {/* ── Footer ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          
          <Button
            type="submit"
            variant="contained"
            startIcon={
              <AddCircleOutline sx={{ fontSize: "1rem !important" }} />
            }
            sx={primaryBtnSx}
          >
            {isEditing ? "Update Authorized Signer" : "Add Authorized Signer"}
          </Button>
          <Button
            onClick={onClose}
            startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
            sx={cancelBtnSx}
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default AuthorizedSignerPopup;